import { useAuth } from '../../contexts/AuthContext';
import { useSiteAdvertisingConfig } from '../../contexts/SiteAdvertisingContext';
import { AdCarousel } from './AdCarousel';
import {
  slotShouldDisplay,
  type AdShell,
  type AdSlotKey,
} from '../../lib/siteAdvertisingConfig';
import { useLocation } from 'react-router-dom';

export interface SiteAdSlotProps {
  slotKey: AdSlotKey;
  shell: AdShell;
}

export function SiteAdSlot({ slotKey, shell }: SiteAdSlotProps) {
  const { pathname } = useLocation();
  const { user, session, loading: authLoading } = useAuth();
  const { config, loading } = useSiteAdvertisingConfig();

  if (loading) return null;
  if (pathname.startsWith('/site-admin')) return null;

  const slot = config[slotKey];
  // Until Supabase auth has resolved, guest vs signed-in is unknown — avoid wrong segment.
  if (
    authLoading &&
    (slot.audience === 'guests_only' || slot.audience === 'signed_in_only')
  ) {
    return null;
  }

  const isAuthenticated = !!(session?.user ?? user);
  if (
    !slotShouldDisplay(slot, {
      pathname,
      isAuthenticated,
      shell,
    })
  ) {
    return null;
  }

  const edge =
    slotKey === 'footer'
      ? 'border-t border-gray-200 dark:border-slate-700'
      : 'border-b border-gray-200 dark:border-slate-700';

  // Space above the footer-rail ad so it sits clearly below the main content breakpoint (e.g. team chat).
  const mainGap = slotKey === 'footer' ? 'mt-6 sm:mt-8' : '';

  return (
    <div className={`w-full ${edge} ${mainGap} bg-gray-50/80 dark:bg-slate-900/40`}>
      <AdCarousel
        slides={slot.slides}
        maxHeightPx={slot.maxHeightPx}
        autoplaySeconds={slot.autoplaySeconds}
        showDots={slot.showDots}
        showArrows={slot.showArrows}
      />
    </div>
  );
}
